import type { AreaPageV2Destination } from '../contracts';
import type { CollectionsTabId } from './collections-tabs';

const STORAGE_KEY = 'area-page-v2:destination';

const DESTINATIONS: readonly string[] = ['map', 'collections', 'insights'];
const COLLECTIONS_TABS: readonly string[] = ['favourites', 'saved-searches', 'history'];

export type RememberedDestination = {
  slug: string;
  destination: AreaPageV2Destination;
  collectionsTab: CollectionsTabId;
};

/**
 * Which workspace the user was in, kept for the browser tab session so a
 * refresh returns to it instead of the map.
 *
 * The area slug is stored with it and only a match is restored, so opening a
 * different area starts on the map. One entry is kept rather than one per
 * area, so visiting several areas does not fill up storage.
 */
export function rememberDestination(value: RememberedDestination): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage is unavailable in private windows and when the quota is full.
    // Forgetting the workspace is not worth failing a render over.
  }
}

/** The remembered workspace for `slug`, or null when there is nothing usable. */
export function readRememberedDestination(slug: string): RememberedDestination | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }

    // Anything can be in storage, so every field is checked against the values
    // this page knows rather than trusted.
    const parsed = JSON.parse(stored) as Partial<RememberedDestination> | null;
    if (!parsed || parsed.slug !== slug || !DESTINATIONS.includes(parsed.destination as string)) {
      return null;
    }

    return {
      slug,
      destination: parsed.destination as AreaPageV2Destination,
      collectionsTab: COLLECTIONS_TABS.includes(parsed.collectionsTab as string)
        ? parsed.collectionsTab as CollectionsTabId
        : 'favourites',
    };
  } catch {
    return null;
  }
}
